import { ReactElement, ReactNode, useCallback, useMemo } from 'react';
import { useToast } from '@cad-arc';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { observer } from 'mobx-react-lite';
import { AdditionalInfosStoreProvider } from '../../../additional-infos/hooks/use-additional-infos/additional-infos-provider';
import { ShowErrorAlertProvider } from '../../../additional-infos/hooks/use-show-error-alert/use-show-error-alert';
import { AdditionalInfoType } from '../../../additional-infos/types';
import { SortDirection } from '../../../additional-infos/hooks/use-additional-infos/types';
import { useCurrentUser } from '../../../current-user/current-user';
import { useCadNextAdditionalInfoNotifications } from '../../../event-notifications/use-additional-info-notifications';
import { useBoloPanelMetadata } from '../state/bolo-panel-metadata-context';

export const BoloPanelAdditionalInfosProvider = observer(
    ({ children }: { children: ReactNode }): ReactElement => {
        const metadata = useBoloPanelMetadata();
        const { userId } = useCurrentUser();
        const notificationManager = useCadNextAdditionalInfoNotifications();
        const toast = useToast();
        const { defaultNarrativeDirectionNewOnTop } = useEnabledFeatures();
        const noSystemMessagesByDefault = useEnabledFeatures()?.[
            'filterOutSystemMessagesFromNarrativeByDefault'
        ];
        const initialSortDirection =
            defaultNarrativeDirectionNewOnTop === false ? SortDirection.ASC : SortDirection.DESC;

        const showErrorAlert = useCallback(
            ({ message: description }) => {
                toast({
                    description,
                    status: 'error',
                });
            },
            [toast]
        );

        const initialFilters = useMemo(() => {
            const initialFilters = [AdditionalInfoType.USER];

            if (!noSystemMessagesByDefault) {
                initialFilters.push(AdditionalInfoType.SYSTEM);
            }

            return initialFilters;
        }, [noSystemMessagesByDefault]);

        if (metadata?.boloId === undefined) {
            // eslint-disable-next-line react/jsx-no-useless-fragment
            return <>{children}</>;
        }

        return (
            <ShowErrorAlertProvider showErrorAlert={showErrorAlert}>
                <AdditionalInfosStoreProvider
                    isBolo
                    entityId={metadata?.boloId}
                    entityNumber={metadata?.boloNumber}
                    currentUserId={userId}
                    notificationManager={notificationManager}
                    initialFilter={initialFilters}
                    initialSortDirection={initialSortDirection}
                    enablePinnedEntries
                >
                    {children}
                </AdditionalInfosStoreProvider>
            </ShowErrorAlertProvider>
        );
    }
);
